import i18n from '../../../i18n';
import type { DataAnalysis } from '../../../models';

/** 退菜列表行：含 `is_history`、`status`、`server_id` */
export type RefundDishRow = DataAnalysis & {
  /** 是否历史订单退菜：false=正常退菜；true 时结合 status 区分取消订单/正常退菜 */
  is_history?: boolean | number | null;
  status?: number | null;
  /** 云端历史订单详情接口 id */
  server_id?: string | number | null;
};

/**
 * 将接口 `is_history` 归一为 boolean。
 * @param value - 接口原值
 */
function coerceRefundIsHistory(value: unknown): boolean {
  if (value === true || value === 1 || value === '1') {
    return true;
  }
  if (value === false || value === 0 || value === '0') {
    return false;
  }
  return false;
}

/**
 * 退菜原因类型：0=取消订单，1=正常退菜。
 * - `is_history === false`：一律正常退菜；
 * - `is_history === true` 且 `status === 0`：取消订单；
 * - `is_history === true` 且 `status === 1`（或其它非 0）：正常退菜。
 * @param row - 退菜列表行
 */
export function normalizeRefundReasonStatus(row: RefundDishRow): 0 | 1 {
  const isHistory = coerceRefundIsHistory(row.is_history);
  if (!isHistory) {
    return 1;
  }
  if (row.status == null) {
    return 1;
  }
  return Number(row.status) === 0 ? 0 : 1;
}

/**
 * 退菜原因列展示：`return_reason` 在前；括号内为类型文案。
 * @param row - 退菜行
 */
export function getRefundDishReasonDisplayText(row: RefundDishRow): string {
  const raw = (row.return_reason ?? '').trim();
  const typeLabel =
    normalizeRefundReasonStatus(row) === 0
      ? i18n.t('order_page_text_cancel_order')
      : i18n.t('business_analysis_page_refund_status_normal');
  return raw ? `${raw}（${typeLabel}）` : `（${typeLabel}）`;
}

/**
 * 云端时段退菜：未返回 `is_history` 时补 true（历史订单数据源）。
 * @param row - 接口单条
 */
export function withCloudRefundHistoryFlag(row: DataAnalysis): RefundDishRow {
  if (row.is_history != null && row.is_history !== undefined) {
    return row as RefundDishRow;
  }
  return { ...row, is_history: true };
}

/**
 * 即时退菜：未返回 `is_history` 时补 false（当前隧道订单语义）。
 * @param row - 接口单条
 */
export function withInstantRefundHistoryFlag(row: DataAnalysis): RefundDishRow {
  if (row.is_history != null && row.is_history !== undefined) {
    return row as RefundDishRow;
  }
  return { ...row, is_history: false };
}

/**
 * 订单 ID（order_head_id）展示。
 * @param row - 退菜行
 */
export function formatRefundOrderHeadId(row: DataAnalysis): string {
  return row.order_head_id != null ? String(row.order_head_id) : '';
}

/**
 * 订单号：优先 check_id，否则 check_number。
 * @param row - 退菜行
 */
export function formatRefundCheckId(row: DataAnalysis): string {
  const checkId = row.check_id;
  if (checkId != null && String(checkId).trim() !== '') {
    return String(checkId);
  }
  const checkNumber = row.check_number;
  if (checkNumber != null && String(checkNumber).trim() !== '') {
    return String(checkNumber);
  }
  return '';
}
